import { useVidarioClient } from "@vidario/api-client";
import { type JobCreateInput } from "@vidario/domain";
import { Button, Card, Input, Spinner } from "@vidario/ui";
import { Check, Link as LinkIcon, Sparkles, Upload } from "lucide-react";
import { useRef, useState, type ReactElement } from "react";

/** Result of a parse — job fields used to pre-fill the create form. */
export interface ImportResult {
  values: Partial<JobCreateInput>;
}

export interface ImportCardProps {
  onImported: (result: ImportResult) => void;
  onImportStart?: () => void;
  onImportError?: (message: string) => void;
}

type Mode = "url" | "pdf";

/** Auto-import card — MVP_requirements.md §4 Screen 2. Sends a job-posting
 * URL or an uploaded PDF to the backend `/jobs/parse/` endpoint, which
 * extracts the structured fields via Claude and returns them to pre-fill
 * the form. Parsing is synchronous: the recruiter waits with a spinner. */
export function ImportCard({
  onImported,
  onImportStart,
  onImportError,
}: ImportCardProps): ReactElement {
  const client = useVidarioClient();
  const [mode, setMode] = useState<Mode>("url");
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canImportUrl = url.trim().length > 0;

  const runImport = (source: { url: string } | { file: File }): void => {
    if (importing) return;
    setImporting(true);
    onImportStart?.();
    client
      .parseJob(source)
      .then((result) => onImported(result))
      .catch((err: unknown) => {
        onImportError?.(
          err instanceof Error
            ? err.message
            : "Import fehlgeschlagen — bitte Felder manuell ergänzen.",
        );
      })
      .finally(() => setImporting(false));
  };

  const handleFile = (file: File | undefined): void => {
    if (file) runImport({ file });
  };

  return (
    <Card className="overflow-hidden bg-gradient-to-b from-[#FAFBFD] to-neutral-0 p-0" padded={false}>
      <div className="px-7 pb-5 pt-6">
        <div className="mb-1.5 flex items-center gap-2.5">
          <span className="inline-flex h-7.5 w-7.5 items-center justify-center rounded-md bg-primary-500 text-neutral-0">
            <Sparkles size={16} strokeWidth={2} />
          </span>
          <h3 className="m-0 text-base font-semibold">Stelle automatisch importieren</h3>
          <span className="inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-primary-700">
            KI
          </span>
        </div>
        <p className="mb-5 ml-10 mt-0 text-[13px] text-text-tertiary">
          Vidario extrahiert Jobtitel, Anforderungen, Gehaltsspanne und Seniorität aus deiner Quelle.
        </p>

        <div className="mb-4 flex gap-1.5 border-b border-border-default">
          {(
            [
              { id: "url" as const, label: "URL", Icon: LinkIcon },
              { id: "pdf" as const, label: "PDF-Upload", Icon: Upload },
            ] satisfies { id: Mode; label: string; Icon: typeof LinkIcon }[]
          ).map(({ id, label, Icon }) => {
            const active = mode === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                aria-current={active ? "page" : undefined}
                className={
                  "-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-[13px] transition-colors " +
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 " +
                  (active
                    ? "border-primary-500 font-semibold text-primary-700"
                    : "border-transparent font-medium text-neutral-500 hover:text-neutral-800")
                }
              >
                <Icon size={14} /> {label}
              </button>
            );
          })}
        </div>

        {mode === "url" ? (
          <div className="flex items-end gap-2.5">
            <div className="flex-1">
              <Input
                icon={<LinkIcon size={16} />}
                placeholder="https://finovia.de/karriere/senior-backend-engineer"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                aria-label="Stellenausschreibungs-URL"
                type="url"
              />
            </div>
            <Button
              loading={importing}
              disabled={!canImportUrl || importing}
              onClick={() => runImport({ url: url.trim() })}
            >
              {importing ? "Extrahiere Daten…" : "Importieren"}
            </Button>
          </div>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
              disabled={importing}
              className={
                "w-full rounded-xl border-2 border-dashed border-border-strong px-6 py-8 text-center transition-colors " +
                "hover:border-primary-400 hover:bg-primary-50 " +
                "disabled:cursor-default disabled:opacity-60 " +
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              }
            >
              <Upload size={24} className="mx-auto text-neutral-400" />
              <div className="mt-2.5 text-sm font-medium">PDF hierhin ziehen</div>
              <div className="mt-1 text-xs text-text-tertiary">
                oder klicken, um eine Datei auszuwählen
              </div>
            </button>
          </>
        )}

        {importing && (
          <div
            role="status"
            data-testid="import-progress"
            className="mt-4 flex items-center gap-3 rounded-[10px] bg-primary-50 p-3.5 text-[13px] text-primary-700"
          >
            <Spinner size={16} className="text-primary-600" />
            <div>
              <b>Extrahiere Stellendaten…</b>
              <div className="mt-0.5 text-xs opacity-80">
                Quelle wird analysiert · Skills &amp; Gehalt werden erkannt
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export const PARSER_HIGHLIGHTS = [
  "Jobtitel, Unternehmen, Branche",
  "Standort & Beschäftigungsart",
  "Aufgaben & Benefits",
  "Anforderungen mit Wichtigkeit & Knock-out",
  "Gehaltsspanne & Einstellungsdatum",
] as const;

export function ParserChecklist(): ReactElement {
  return (
    <ul className="m-0 flex flex-col gap-2 p-0 list-none">
      {PARSER_HIGHLIGHTS.map((f) => (
        <li
          key={f}
          className="inline-flex items-center gap-2 text-[13px] text-primary-800"
        >
          <Check size={13} strokeWidth={2.5} className="text-success-500" />
          {f}
        </li>
      ))}
    </ul>
  );
}
